import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { del, get, post } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";
import { trackSponsorTipsEvent } from "../../Utils/sponsorTipsConstants";
import type {
  SponsorTip,
  SponsorTipDetailResponse,
  SponsorTipListResponse,
  SponsorTipRecommendedResponse,
} from "../../Utils/sponsorTipsConstants";

type ListCache = {
  tips: SponsorTip[];
  total: number;
  loading: boolean;
  error: string | null;
  loaded: boolean;
};

type AsyncDetailState = {
  data: SponsorTipDetailResponse | null;
  loading: boolean;
  error: string | null;
  slug: string | null;
};

type BookmarksState = {
  slugs: string[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
};

interface SponsorTipsState {
  listsByAudience: Record<string, ListCache>;
  recommended: ListCache;
  detail: AsyncDetailState;
  bookmarks: BookmarksState;
}

const emptyList = (): ListCache => ({
  tips: [],
  total: 0,
  loading: false,
  error: null,
  loaded: false,
});

const emptyDetail = (): AsyncDetailState => ({
  data: null,
  loading: false,
  error: null,
  slug: null,
});

const emptyBookmarks = (): BookmarksState => ({
  slugs: [],
  loading: false,
  loaded: false,
  error: null,
});

const initialState: SponsorTipsState = {
  listsByAudience: {},
  recommended: emptyList(),
  detail: emptyDetail(),
  bookmarks: emptyBookmarks(),
};

const getBookmarkUserId = (state: {
  authentication: { user?: { user?: { user_id?: string } } };
}): string => {
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  return String(
    state.authentication.user?.user?.user_id ||
      loggedInUser?.userId ||
      loggedInUser?.user_id ||
      ""
  );
};

const toErrorMessage = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (error?.message && typeof error.message === "string") {
    try {
      const parsed = JSON.parse(error.message);
      if (parsed?.message) return parsed.message;
    } catch {
      return error.message;
    }
  }
  return fallback;
};

const unwrapList = (response: any): SponsorTipListResponse => {
  const data = response?.data ?? response;
  return {
    tips: data?.tips ?? [],
    total: data?.total ?? data?.tips?.length ?? 0,
  };
};

const unwrapRecommended = (response: any): SponsorTipRecommendedResponse => {
  const data = response?.data ?? response;
  return {
    tips: data?.tips ?? [],
    total: data?.total ?? data?.tips?.length ?? 0,
    sponsor_context: data?.sponsor_context,
  };
};

const unwrapDetail = (response: any): SponsorTipDetailResponse =>
  response?.data ?? response;

export type SponsorTipsAudienceParams = {
  audience: string;
  force?: boolean;
};

export const fetchSponsorTipsForAudience = createAsyncThunk(
  "sponsorTips/fetchForAudience",
  async (
    { audience, force = false }: SponsorTipsAudienceParams,
    { getState, rejectWithValue }
  ) => {
    const { sponsorTips } = getState() as { sponsorTips: SponsorTipsState };
    const cached = sponsorTips.listsByAudience[audience];
    if (cached?.loaded && !force) {
      return { audience, skipped: true as const, ...cached };
    }

    try {
      const response = await get(API_URLS.sponsorTipsList({ audience }));
      const payload = unwrapList(response);
      return { audience, skipped: false as const, ...payload };
    } catch (error: any) {
      return rejectWithValue({
        audience,
        message: toErrorMessage(
          error,
          "Unable to load guidance. Please try again."
        ),
      });
    }
  }
);

export const fetchSponsorTipsRecommended = createAsyncThunk(
  "sponsorTips/fetchRecommended",
  async (force: boolean | undefined, { getState, rejectWithValue }) => {
    const { sponsorTips } = getState() as { sponsorTips: SponsorTipsState };
    const cached = sponsorTips.recommended;
    if (cached.loaded && !force) {
      return { skipped: true as const, ...cached };
    }

    try {
      const response = await get(
        API_URLS.sponsorTipsRecommended({ limit: 24 })
      );
      const payload = unwrapRecommended(response);
      return { skipped: false as const, ...payload };
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Personalized recommendations unavailable")
      );
    }
  }
);

export const fetchSponsorTipBySlug = createAsyncThunk(
  "sponsorTips/fetchBySlug",
  async (slug: string, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.sponsorTipBySlug(slug));
      return { slug, detail: unwrapDetail(response) };
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Tip not found or unavailable.")
      );
    }
  }
);

export const fetchSponsorTipsBookmarks = createAsyncThunk(
  "sponsorTips/fetchBookmarks",
  async (force: boolean | undefined, { getState, rejectWithValue }) => {
    const state = getState() as {
      sponsorTips: SponsorTipsState;
      authentication: { user?: { user?: { user_id?: string } } };
    };
    const userId = getBookmarkUserId(state);
    if (!userId) {
      return { skipped: true as const, slugs: [] as string[] };
    }
    if (state.sponsorTips.bookmarks.loaded && !force) {
      return {
        skipped: true as const,
        slugs: state.sponsorTips.bookmarks.slugs,
      };
    }

    try {
      const response: any = await get(API_URLS.sponsorTipsBookmarks());
      const slugs = response?.slugs;
      return {
        skipped: false as const,
        slugs: Array.isArray(slugs)
          ? slugs.filter(
              (value: unknown): value is string => typeof value === "string"
            )
          : [],
      };
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Unable to load saved guidance.")
      );
    }
  }
);

export type ToggleSponsorTipBookmarkPayload = {
  slug: string;
  wasSaved: boolean;
};

export const toggleSponsorTipBookmark = createAsyncThunk(
  "sponsorTips/toggleBookmark",
  async (
    { slug, wasSaved }: ToggleSponsorTipBookmarkPayload,
    { getState, rejectWithValue }
  ) => {
    const state = getState() as {
      sponsorTips: SponsorTipsState;
      authentication: { user?: { user?: { user_id?: string } } };
    };
    const userId = getBookmarkUserId(state);
    if (!userId || !slug) {
      return rejectWithValue({ slug, wasSaved, noOp: true });
    }

    try {
      if (wasSaved) {
        await del(API_URLS.sponsorTipsBookmarkBySlug(slug));
      } else {
        await post(API_URLS.sponsorTipsBookmarkBySlug(slug), {});
      }
      trackSponsorTipsEvent(wasSaved ? "bookmark_removed" : "bookmark_added", {
        slug,
        storage: "db",
      });
      return { slug, wasSaved };
    } catch (error: any) {
      return rejectWithValue({
        slug,
        wasSaved,
        message: toErrorMessage(error, "Unable to update bookmark."),
      });
    }
  }
);

const ensureAudienceSlot = (
  state: SponsorTipsState,
  audience: string
): ListCache => {
  if (!state.listsByAudience[audience]) {
    state.listsByAudience[audience] = emptyList();
  }
  return state.listsByAudience[audience];
};

const SponsorTipsSlice = createSlice({
  name: "sponsorTips",
  initialState,
  reducers: {
    clearSponsorTipDetail: state => {
      state.detail = emptyDetail();
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSponsorTipsForAudience.pending, (state, action) => {
        const slot = ensureAudienceSlot(state, action.meta.arg.audience);
        slot.loading = true;
        slot.error = null;
      })
      .addCase(fetchSponsorTipsForAudience.fulfilled, (state, action) => {
        const { audience, skipped, tips, total } = action.payload;
        const slot = ensureAudienceSlot(state, audience);
        slot.loading = false;
        if (!skipped) {
          slot.tips = tips;
          slot.total = total;
          slot.loaded = true;
        }
        slot.error = null;
      })
      .addCase(fetchSponsorTipsForAudience.rejected, (state, action) => {
        const payload = action.payload as { audience: string; message: string };
        const audience = payload?.audience || action.meta.arg.audience;
        const slot = ensureAudienceSlot(state, audience);
        slot.loading = false;
        slot.error =
          payload?.message || "Unable to load guidance. Please try again.";
      })
      .addCase(fetchSponsorTipsRecommended.pending, state => {
        state.recommended.loading = true;
        state.recommended.error = null;
      })
      .addCase(fetchSponsorTipsRecommended.fulfilled, (state, action) => {
        state.recommended.loading = false;
        if (!action.payload.skipped) {
          state.recommended.tips = action.payload.tips;
          state.recommended.total = action.payload.total;
          state.recommended.loaded = true;
        }
        state.recommended.error = null;
      })
      .addCase(fetchSponsorTipsRecommended.rejected, (state, action) => {
        state.recommended.loading = false;
        state.recommended.error =
          (action.payload as string) ||
          "Personalized recommendations unavailable";
      })
      .addCase(fetchSponsorTipBySlug.pending, (state, action) => {
        state.detail.loading = true;
        state.detail.error = null;
        state.detail.slug = action.meta.arg;
      })
      .addCase(fetchSponsorTipBySlug.fulfilled, (state, action) => {
        state.detail.loading = false;
        state.detail.data = action.payload.detail;
        state.detail.slug = action.payload.slug;
        state.detail.error = null;
      })
      .addCase(fetchSponsorTipBySlug.rejected, (state, action) => {
        state.detail.loading = false;
        state.detail.data = null;
        state.detail.error =
          (action.payload as string) || "Tip not found or unavailable.";
      })
      .addCase(fetchSponsorTipsBookmarks.pending, state => {
        state.bookmarks.loading = true;
        state.bookmarks.error = null;
      })
      .addCase(fetchSponsorTipsBookmarks.fulfilled, (state, action) => {
        state.bookmarks.loading = false;
        state.bookmarks.loaded = true;
        if (!action.payload.skipped) {
          state.bookmarks.slugs = action.payload.slugs;
        }
        state.bookmarks.error = null;
      })
      .addCase(fetchSponsorTipsBookmarks.rejected, (state, action) => {
        state.bookmarks.loading = false;
        state.bookmarks.loaded = true;
        state.bookmarks.slugs = [];
        state.bookmarks.error =
          (action.payload as string) || "Unable to load saved guidance.";
      })
      // Optimistic update: flip the bookmark in Redux as soon as the request
      // starts so the UI feels instant. If the API fails, rejected rolls this back.
      .addCase(toggleSponsorTipBookmark.pending, (state, action) => {
        const { slug, wasSaved } = action.meta.arg;
        if (!slug) return;
        const idx = state.bookmarks.slugs.indexOf(slug);
        if (wasSaved) {
          if (idx >= 0) state.bookmarks.slugs.splice(idx, 1);
        } else if (idx < 0) {
          state.bookmarks.slugs.push(slug);
        }
      })
      // Success: re-confirm the optimistic bookmark state from the payload
      // (idempotent). API returns no extra fields beyond slug / wasSaved.
      .addCase(toggleSponsorTipBookmark.fulfilled, (state, action) => {
        const { slug, wasSaved } = action.payload as {
          slug: string;
          wasSaved: boolean;
        };
        if (!slug) return;
        const idx = state.bookmarks.slugs.indexOf(slug);
        if (wasSaved) {
          if (idx >= 0) state.bookmarks.slugs.splice(idx, 1);
        } else if (idx < 0) {
          state.bookmarks.slugs.push(slug);
        }
        state.bookmarks.error = null;
      })
      .addCase(toggleSponsorTipBookmark.rejected, (state, action) => {
        const payload = action.payload as
          | { slug: string; wasSaved: boolean; noOp?: boolean }
          | undefined;
        if (!payload?.slug || payload.noOp) return;
        const { slug, wasSaved } = payload;
        const idx = state.bookmarks.slugs.indexOf(slug);
        if (wasSaved) {
          if (idx < 0) state.bookmarks.slugs.push(slug);
        } else if (idx >= 0) {
          state.bookmarks.slugs.splice(idx, 1);
        }
      });
  },
});

export const { clearSponsorTipDetail } = SponsorTipsSlice.actions;

export default SponsorTipsSlice.reducer;
